/* eslint-disable */
// useAttachmentEntities — resolve {kind, id} refs into renderable rows, in the
// READER's own session.
//
// ⚠️ THIS IS THE RULE THE WHOLE FEATURE EXISTS AROUND. The notification row is
// ONE row shared by every recipient. An image URL placed in its payload would
// be resolved once, at send time, under the SENDER's permissions — and would
// then belong to everyone who ever reaches that row, including the people the
// access filter deliberately excluded. The platform's promise is "no access, no
// notification"; a picture is content, and a link in the payload walks it out
// of that promise silently.
//
// So: the payload contributes a `kind` (matched against a fixed registry) and
// an `id` (a parameterised .in() value). Everything shown — code, name, detail,
// picture — comes from the row fetched HERE, under the reader's own RLS. A
// reader who cannot see the model gets zero rows back, which is not an error:
// it is the system working, and it renders as "no longer available to you".
//
// ⚠️ ONE QUERY PER FAMILY ACROSS THE WHOLE VISIBLE LIST, NOT PER NOTIFICATION.
// The first cut batched per component, which MEASURED at 14 requests for a
// drawer of 7 rows — the shape §5.5 forbids ("ten notifications must not fire
// forty requests"). Every mounted instance now feeds ONE module-level
// coalescer: ids arriving inside the same short window are pooled per family,
// deduped, and fetched once. Ten notifications with four attachments each cost
// four requests, not forty.

import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../supabase';
import { ATTACH_FAMILIES, familyFor } from '../utils/attachmentRegistry';
import { chunkIds, PG_IN_MAX } from '../utils/pgIn';

// ---------------------------------------------------------------------------
// The coalescer. Module-level on purpose: its whole job is to see the requests
// that separate React components make independently, which a hook-local batcher
// by definition cannot.
const WINDOW_MS = 20;                 // long enough for one render pass
// Keyed by kind AND by which column the id addresses, because a legacy
// lens.favorited pair addresses lens_no while a P6 attachment addresses the
// primary key. Pooling them together would query the wrong column for half the
// batch and silently resolve nothing.
const pending = new Map();            // 'kind:col' -> { ids:Set, waiters:[fn], timer }
const cache = new Map();              // 'kind|id' (+'|e' for entity-id refs) -> row | null

function flush(poolKey) {
  const slot = pending.get(poolKey);
  if (!slot) return;
  pending.delete(poolKey);
  const [kind, mode] = poolKey.split(':');
  const fam = familyFor(kind);
  const byEntityId = mode === 'e';
  const col = byEntityId ? fam.entityIdCol : fam.idCol;
  const ids = [...slot.ids];

  (async () => {
    const found = {};
    try {
      for (const chunk of chunkIds(ids, PG_IN_MAX)) {
        let q = supabase.from(fam.table).select(fam.select).in(col, chunk);
        if (fam.activeOnly) q = fam.activeOnly(q);
        const { data, error } = await q;
        if (error) throw error;
        (data || []).forEach((row) => {
          found[String(row[col])] = {
            kind,
            id: String(row[fam.idCol]),
            code: fam.code(row),
            name: fam.name(row),
            detail: fam.detail(row),
            image: fam.image(row),
          };
        });
      }
    } catch (e) {
      // A family that fails leaves its ids unresolved, which renders as "no
      // longer available" — the same words RLS-hidden produces. Deliberate: the
      // reader cannot act on the difference, and a second failure state would
      // only be noise.
    }
    // Every id asked for gets an answer, so "asked and got nothing" is
    // distinguishable from "not asked yet".
    const suffix = byEntityId ? '|e' : '';
    ids.forEach((id) => { cache.set(kind + '|' + id + suffix, found[id] || null); });
    slot.waiters.forEach((fn) => { try { fn(); } catch (_) {} });
  })();
}

function request(kind, mode, ids, onDone) {
  const fam = familyFor(kind);
  if (!fam) return;
  if (mode === 'e' && !fam.entityIdCol) return;
  const poolKey = kind + ':' + mode;
  let slot = pending.get(poolKey);
  if (!slot) {
    slot = { ids: new Set(), waiters: [], timer: null };
    pending.set(poolKey, slot);
    slot.timer = setTimeout(() => flush(poolKey), WINDOW_MS);
  }
  ids.forEach((id) => slot.ids.add(id));
  slot.waiters.push(onDone);
}

// ---------------------------------------------------------------------------
// refs: [{kind, id}] — may repeat across notifications; deduped here and again
// in the coalescer.
export function useAttachmentEntities(refs) {
  const [, bump] = useState(0);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const sig = useMemo(() => {
    const s = new Set();
    (refs || []).forEach((r) => {
      if (r && familyFor(r.kind)) s.add(r.kind + '|' + r.id + (r.byEntityId ? '|e' : ''));
    });
    return [...s].sort().join(',');
  }, [refs]);

  useEffect(() => {
    if (!sig) return;
    const missing = {};
    sig.split(',').forEach((k) => {
      if (cache.has(k)) return;
      const [kind, id, e] = k.split('|');
      const poolKey = kind + ':' + (e === 'e' ? 'e' : 'i');
      (missing[poolKey] = missing[poolKey] || []).push(id);
    });
    const pools = Object.keys(missing);
    if (!pools.length) { bump((n) => n + 1); return; }
    pools.forEach((poolKey) => {
      const [kind, mode] = poolKey.split(':');
      request(kind, mode, missing[poolKey], () => {
        if (alive.current) bump((n) => n + 1);
      });
    });
  }, [sig]);

  // Computed during render, not memoised: `cache` is a module-level Map, so a
  // dependency array cannot see it change. The bump() above is what schedules
  // the re-render; this just reads the Map at that moment. It is a handful of
  // keys — cheap enough that caching it would cost more than it saves.
  const byKey = {};
  if (sig) sig.split(',').forEach((k) => { byKey[k] = cache.has(k) ? cache.get(k) : undefined; });

  const ready = !sig || sig.split(',').every((k) => cache.has(k));
  return { byKey, state: ready ? 'ready' : 'loading' };
}

export default useAttachmentEntities;
